import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import ArtboardContainer from 'components/ArtboardContainer';
import DragLayer from 'components/DragLayer';
import Header from 'components/Header';
import LoadingOverlay from 'components/LoadingOverlay';
import Main from 'components/Main';
import ScreensPanel from 'components/ScreensPanel';
import { AppRoute, ProjectOptionType, ProjectsConfig } from 'const';
import ProjectPanel from 'containers/ProjectPanel';
import { validateEventKey } from 'containers/UndoRedoControl/utils';
import { getProjectData } from 'containers/WelcomeScreen/actions';
import { useProjectType } from 'hooks/useProjectType';
import { outsideBackgroundColor as getOutsideBackgroundColor } from 'utils/converters';
import { eventEmitter, EMITTER_EVENTS } from 'utils/eventEmitter';
import { isUnsupportedBrowser } from 'utils/isUnsupportedBrowser';
import { getBackgroundColor } from 'utils/styles';
import { projectScreenSelector } from './selectors';
import styles from './styles.module.scss';

const PROJECT_ID_PATH_REGEX = new RegExp(`${AppRoute.PROJECT}/(\\d+)`);

const ProjectScreen: React.FunctionComponent<RouteComponentProps> = (props) => {
  const { history, location } = props;
  const dispatch = useDispatch();
  const {
    isLocked,
    activeProcessType,
    options,
    activeArtboard,
    artboardBackgroundFromMasterScreen,
    colors,
    rootDocument,
    showScreensPanel,
    showPreview,
    scale,
  } = useSelector(projectScreenSelector);

  const rootDocumentId = rootDocument.get('id');

  React.useEffect(
    () => {
      // to enable browser refreshing within an opened project
      // TODO: maybe need to redirect to welcome page
      if (isUnsupportedBrowser()) {
        history.replace(`/${AppRoute.UNSUPPORTED_BROWSER}`);

        return;
      }
      if (rootDocumentId || isLocked) {
        return;
      }
      const projectPathMatch = PROJECT_ID_PATH_REGEX.exec(location.pathname);
      if (!projectPathMatch) {
        return;
      }
      const [, projectIdStr] = projectPathMatch;
      const projectId = Number(projectIdStr);
      if (isNaN(projectId)) {
        return;
      }
      dispatch(getProjectData(projectId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const KEYDOWN_EVENTNAME = 'keydown';

  function handleBodyKeydown(event: KeyboardEvent): void {
    if (event.target === document.body && validateEventKey(event)) {
      event.preventDefault();

      eventEmitter.emit(EMITTER_EVENTS.BODY_KEYDOWN, event);
    }
  }

  React.useEffect(
    () => {
      window.addEventListener(KEYDOWN_EVENTNAME, handleBodyKeydown);

      return (): void => {
        window.removeEventListener(KEYDOWN_EVENTNAME, handleBodyKeydown);
      };
    },
    [],
  );

  const projectType = useProjectType();
  const {
    backgroundColor: outsideBackgroundColor,
  } = getBackgroundColor(getOutsideBackgroundColor(colors, activeArtboard), artboardBackgroundFromMasterScreen);
  const { areScreensResizable } = ProjectsConfig[projectType] || {} as ProjectOptionType;
  const artboardHeight = areScreensResizable
    ? activeArtboard && activeArtboard.getIn(['styles', 'height'])
    : undefined;

  return (
    <div className={styles.ProjectScreen}>
      {
        rootDocumentId &&
        <>
          <Header rootDocument={rootDocument} />
          <Main
            artboardBackground={outsideBackgroundColor}
            artboardHeight={artboardHeight}
            showScreensPanel={showScreensPanel}
            showPreview={showPreview}
            scale={scale}
            content={<ArtboardContainer />}
            assetPanel={<ProjectPanel />}
            screensPanel={<ScreensPanel />}
          />
        </>
      }
      <DragLayer />
      <LoadingOverlay isLocked={isLocked} activeProcessType={activeProcessType} options={options} />
    </div>
  );
};

export default ProjectScreen;
